import React, { JSX, useContext } from "react";
import { GetServerSideProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { fetchCollectionVariants } from "utils/fetchCollectionVariants";
import { LogContext } from "context/log";
import { CollectionLayout } from "components/layout/CollectionLayout";
import { Heading } from "@pepdirect/v3/ui/Heading";
import { BundleTile } from "components/BundleTile";
import createSsrClient from "services/graphql/ssr-client";
import { VariantFragment } from "services/graphql/generated";
import { useSaveScroll } from "@pepdirect/v3/hooks/useScrollRestoration";
import { PAGE_TYPE_ENUM } from "@pepdirect/v3/types";
import { META_TITLE, CANONICAL_URL_BASE } from "constants/meta";
import st from "./kit.module.scss";

interface KitsPageProps {
  variants: VariantFragment[];
  slug: string;
  title: string;
}

export const getServerSideProps: GetServerSideProps<KitsPageProps> = async (
  appContext
) => {
  const gqlClient = createSsrClient(appContext.req);
  const slug = appContext.params?.slug as string;

  if (!slug) {
    return { notFound: true };
  }

  try {
    const result = await fetchCollectionVariants(slug, gqlClient);

    return {
      props: {
        variants: result.variants,
        slug: result.slug,
        title: result.title,
      },
    };
  } catch (e) {
    console.error("Error fetching variants:", e);
    return { notFound: true };
  }
};

export default function Kit({
  variants,
  slug,
  title,
}: KitsPageProps): JSX.Element {
  const { useLogPageView } = useContext(LogContext);
  const router = useRouter();
  useSaveScroll(router);

  const metaTitle = `${title} – ${META_TITLE}`;
  const metaUrl = `${CANONICAL_URL_BASE}/kit/${slug}`;

  useLogPageView(
    {
      pageType: PAGE_TYPE_ENUM.collection,
      pageTitle: metaTitle,
      pageCategory: title || undefined,
      id: slug || "",
    },
    undefined,
    undefined,
    slug || ""
  );

  return (
    <div className={st.wrapper}>
      <Head>
        <title>{metaTitle}</title>
        <meta name="og:title" content={metaTitle} key="og:title" />
        <link rel="canonical" href={metaUrl} />
        <meta name="og:url" content={metaUrl} key="og:url" />
      </Head>
      <div className={st.title}>
        <Heading level="h1" size="xl">
          {title}
        </Heading>
      </div>
      <CollectionLayout>
        {!!variants?.length &&
          variants?.length > 0 &&
          variants
            .filter((x) => x)
            .map((item) => (
              <BundleTile item={item} key={item.gtin} collection={title} />
            ))}
      </CollectionLayout>
    </div>
  );
}
